/**
 * Compact tile grid for AI Studio home sections.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import type { AIStudioHomeTileGroup } from './useAIStudioHomeTiles';

interface AIStudioHomeTileGridProps {
  groups: AIStudioHomeTileGroup[];
}

export const AIStudioHomeTileGrid: React.FC<AIStudioHomeTileGridProps> = ({ groups }) => {
  const { t } = useLanguage();

  return (
    <div className="flex flex-col gap-6">
      {groups.map((group) => (
        <section key={group.id} aria-labelledby={`ai-studio-home-${group.id}`}>
          <h2
            id={`ai-studio-home-${group.id}`}
            className="mb-3 text-sm font-semibold uppercase tracking-wide text-[var(--color-surface-text-secondary)]"
          >
            {t(group.titleKey)}
          </h2>
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {group.tiles.map((tile) => {
              const Icon = tile.icon;
              return (
                <li key={tile.id}>
                  <Link
                    to={tile.route}
                    className="flex h-full items-start gap-3 rounded border border-[var(--color-border)] p-3 transition-colors hover:border-[var(--color-accent-bg)]"
                  >
                    <Icon
                      className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-primary-text)]"
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <h3 className="text-base font-semibold text-[var(--color-surface-text)]">
                        {t(tile.titleKey)}
                      </h3>
                      <p className="mt-0.5 text-sm text-[var(--color-surface-text-secondary)] line-clamp-2">
                        {t(tile.descriptionKey)}
                      </p>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};
